import { Injectable } from '@angular/core';
import { Graph } from './lib/graph';
import { Node } from './lib/node';
import { Edge } from './lib/edge';
import { WeightedSimpleDirectedGraph } from './directed/weighted-simple-directed-graph';
import { WeightedSymmetricDirectedGraph } from './symmetric/weighted-symmetric-directed-graph';


@Injectable({
  providedIn: 'root'
})
/**
 * The service class GraphService is 
 * responsible for storing all types of graphs 
 * in a suitable storage format on a json file. 
 * The class also provides functionality to restore stored graphs.
 */
export class GraphService {


  /**
   *
   * @param graph, graph in which you want to store data from the config file
   * @param config, your json file
   */
  importGraph(graph: Graph, config: any) {
    // console.log('-------------Importing Graph------------------- ');
    let i;

    const rows = config.typespecifics.rows;
    const cols = config.typespecifics.cols;

    const nodes = config.typespecifics.nodes;
    const edges = config.typespecifics.edges;
    const isWeighted: boolean = config.typespecifics.isWeighted;

    for (i in nodes) {
      graph.addNode(nodes[i].row, nodes[i].col, nodes[i].id, nodes[i].name);
      if (nodes[i].id !== undefined && nodes[i].id !== null) {
        Node.updateGlobalId(nodes[i].id);
      }
    }

    //console.log('Importing Edge (on GraphService)');
    for (i in edges) {
      const fromId: number = edges[i].fromId;
      const toId: number = edges[i].toId;
      const isBezier: boolean = edges[i].isBezier;

      const fromNode: Node = graph.getNodePerID(fromId);
      const toNode: Node = graph.getNodePerID(toId);

      graph.addEdge(fromNode, toNode);
      const edge: Edge = graph.getEdge(fromNode, toNode);

      if (isWeighted) {
        edge.setWeight(edges[i].weight);
      }

      //console.log("Edge: " + fromNode.getName() + " " +  fromNode.getId() + ", " 
      //+ toNode.getName() + "  " + toNode.getId());

      /*console.log(edge.toString());*/

      if (isBezier) {
        edge.enableBezier(true);
        const bezierPoint1: [number, number] = [edges[i].bezierPoint1.row, edges[i].bezierPoint1.col];
        const bezierPoint2: [number, number] = [edges[i].bezierPoint2.row, edges[i].bezierPoint2.col];

        const coord1: [number, number] = graph.getGrid().convertCoordinateToRaw(bezierPoint1[0], bezierPoint1[1]);
        const coord2: [number, number] = graph.getGrid().convertCoordinateToRaw(bezierPoint2[0], bezierPoint2[1]);

        // console.log("bezierpoint1: "+ bezierPoint1 +". bezierpoint2: " + bezierPoint2);
        // console.log('Bezieredge: ' + ' coord1: ' + coord1 + '. coord2: ' + coord2);
        edge.bezierPoint.initBezier(coord1, coord2, graph.getGrid());
      }



    }
    graph.update();
  }

  exportGraph(graph: Graph, definition: any){
    this.exportNodesAndEdges(graph, definition);
  }
  /**
  *
  * @param graph nodes and edges from graph will be stored in the json given by definition
  * @param definition your json string.
  */
  exportNodesAndEdges(graph: Graph, definition: any) {
    // console.log("-----------------exporting graph---------------------------")
    definition.typespecifics = {};
    definition.typespecifics.rows = graph.getRows();
    definition.typespecifics.cols = graph.getCols();
    let isWeighted = (graph instanceof WeightedSimpleDirectedGraph
      || graph instanceof WeightedSymmetricDirectedGraph);

    definition.typespecifics.isWeighted = isWeighted;
    
   
    definition.typespecifics.nodes = new Array();
    graph.getNodes().forEach(node => {
      const temp = new Object();
      temp['id'] = node.getId();
      temp['row'] = node.getRowIndex();
      temp['col'] = node.getColIndex();
      temp['name'] = node.getName();

      definition.typespecifics.nodes.push(temp);
    });

    definition.typespecifics.edges = new Array();
    graph.getEdges().forEach(edge => {
      const temp = new Object();
      temp['fromId'] = edge.getFromNode().getId();
      temp['toId'] = edge.getToNode().getId();
      if (edge.isBezierEdge()) {
        temp['isBezier'] = true;
      } else {
        temp['isBezier'] = false;
      }
      //  console.log("Edge: " + edge.getFromNode().getName() +" " + edge.getFromNode().getId()+ ", "+
      //   edge.getToNode().getName() + " " + edge.getToNode().getId());
      if(edge.isBezierEdge()){
      const bezPoint1 = new Object();
      const bezPoint2 = new Object();
      const coord1: [number, number] = edge.bezierPoint.point1Coord;
      const coord2: [number, number] = edge.bezierPoint.point2Coord;

      bezPoint1['row'] = coord1[0];
      bezPoint1['col'] = coord1[1];

      bezPoint2['row'] = coord2[0];
      bezPoint2['col'] = coord2[1];

      temp['bezierPoint1'] = bezPoint1;
      temp['bezierPoint2'] = bezPoint2;
      }
      
      if(isWeighted)
        temp['weight'] = edge.getWeight();

      definition.typespecifics.edges.push(temp);
    });

  }


  exportSourceAndTargetNode(sourceId: number, targetId: number, definition: any) {
    definition.typespecifics.sourceId = sourceId;
    definition.typespecifics.targetId = targetId;
  }

  exportMinPath(minPath: number, definition: any) {
    definition.typespecifics.minPath = minPath;
  }

  exportMST(mst: number, definition: any) {
    definition.typespecifics.mst = mst;
  }

  exportVertexCover(minimalCover: number, definition: any) {
    definition.typespecifics.vertexCover = minimalCover;
  }

  
  exportGraphType(type: string, definition: any) {
    definition.typespecifics.graphType = type;
  }

  importGraphType(config: any): string{
    return config.typespecifics.graphType;
  }

  exportLengthPath(lengthPath: number, definition: any) {
    definition.typespecifics.lengthPath = lengthPath;
  }

  exportUnit(unit: string, definition: any) {
    definition.typespecifics.unit = unit;
  }


  exportMandatoryNodes(mandatoryNodes: Node[], definition: any) {
    definition.typespecifics.mandatoryNodes = new Array();

    mandatoryNodes.forEach(node => {
      const temp = new Object();
      temp['id'] = node.getId();
      definition.typespecifics.mandatoryNodes.push(temp);
    });
  }

  exportPrefixNodes(prefixNodes: Node[], definition: any){
    definition.typespecifics.prefixNodes = new Array();

    prefixNodes.forEach(node => {
      const temp = new Object();
      temp['id'] = node.getId();
      definition.typespecifics.prefixNodes.push(temp);
    });

  }

  getUnit(config: any): string {
    if (config.typespecifics.unit) {
      return config.typespecifics.unit;
    }
    return "";
  }

  getMandatoryNodes(config: any): number[] {

    const mandatoryNodes = config.typespecifics.mandatoryNodes;
    let mandatoryIDS: number[] = [];
    for (let i in mandatoryNodes) {
      if (mandatoryNodes[i].id !== undefined && mandatoryNodes[i].id !== null) {
        let newID: number = mandatoryNodes[i].id;
        mandatoryIDS.push(newID);
      }
    }
    return mandatoryIDS;
  }

  getPrefixNodes(config: any): number[]{
    const prefixNodes = config.typespecifics.prefixNodes;
    let prefixIDS: number[] = [];
    for(let i in prefixNodes){
      if(prefixNodes[i].id !== undefined && prefixNodes[i].id !== null){
        let newID: number = prefixNodes[i].id;
        prefixIDS.push(newID);
      }
    }
    return prefixIDS;
  }


  getVertexCover(config: any): number {
    return config.typespecifics.vertexCover;
  }

  getMST(config: any): number {
    return config.typespecifics.mst;
  }

  getDimension(config: any): [number, number] {
    const rows = config.typespecifics.rows;
    const cols = config.typespecifics.cols;

    return [rows, cols];
  }

  getSourceAndTargetNodeIds(config: any): [number, number] {
    const sourceId: number = config.typespecifics.sourceId;
    const targetId: number = config.typespecifics.targetId;

    return [sourceId, targetId];
  }

  getMinPath(config: any): number {
    return config.typespecifics.minPath;
  }

  getLengthPath(config: any): number {
    return config.typespecifics.lengthPath;
  }

  private getNodePerId(nodes: Node[], id: number): Node {
    let node: Node = null;
    nodes.forEach(n => {
      if (node.getId() === id) {
        node = n;
      }
    });
    return node;
  }


}
